import { get, keyBy, map, orderBy } from "lodash"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"
import { getBetaTypes } from "selectors/missingDataFiles"
import { getIntradayBatchMoment } from "helpers/intraday"
import { PREDICTED_BETA, HISTORICAL_BETA, CUSTOM_BETA } from "../constants"
import { isIntradaySimulationEligible, getBetaInstrumentFromOptions } from "../helpers"

// Neither intraday root accepts an instrument argument, so dynamic beta is day-based only.
function buildDynamicBetaSubquery(hasBetaInstrument) {
  return hasBetaInstrument
    ? `dynamicBeta: beta(instrument: $betaInstrument) {
        date
        custom: predicted
      }`
    : ""
}

function buildDayBasedQuery(betaTypes, hasBetaInstrument) {
  return `#multi-date-beta
query MultiDateBetaQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!${hasBetaInstrument ? ", $betaInstrument: PositionSetInput" : ""}) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      beta {
        date
        ${betaTypes}
      }
      ${buildDynamicBetaSubquery(hasBetaInstrument)}
    }
  }
}`
}

function buildLiveTickQuery(betaTypes) {
  return `#multi-date-beta
query MultiDateBetaIntradayQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      beta {
        date: timestamp
        ${betaTypes}
      }
    }
  }
}`
}

const BATCH_TREND_QUERY_TEMPLATE = (betaTypes) => `#multi-date-beta-batch
query MultiDateBetaBatchQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $batchId: Id) {
  model(id: $modelId) {
    simulation:intradaySimulationBatch(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      beta {
        date
        ${betaTypes}
      }
    }
  }
}`

function toBetaPoint({ predicted, historical }) {
  return { [PREDICTED_BETA]: predicted, [HISTORICAL_BETA]: historical }
}

export function createBetaIntradayBatchTrendQueries(modelId, positionSet, base, batches, options = {}) {
  const betaTypes = getBetaTypes(options.missingDataFiles)
  const query = BATCH_TREND_QUERY_TEMPLATE(betaTypes)

  return batches.map(batch => ({
    key: generateQueryKey("beta.multiDate.batch", { modelId, positionSet, base, on: batch.date, batchId: batch.id }),
    skipInvalidation: true,
    query,
    variables: { modelId, positionSet, base, on: batch.date, batchId: batch.id },
    select: (graphqlResult) => {
      const [row] = get(graphqlResult, "data.model.simulation.beta", [])
      const batchMoment = getIntradayBatchMoment(batch)

      return {
        values: {
          // Drop rather than chart a malformed asOf — an Invalid Date would corrupt sort order.
          dates: row && batchMoment.isValid() ? [{
            date: batchMoment.toISOString(),
            batchId: batch.id,
            ...toBetaPoint(row),
          }] : [],
        },
      }
    },
  }))
}

export function createBetaMultiDateQuery(modelId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)
  const betaTypes = getBetaTypes(options.missingDataFiles)
  const betaInstrument = getBetaInstrumentFromOptions({ betaInstrument: options.betaInstrument, isIntraday: shouldUseIntradaySimulation })
  const hasBetaInstrument = Boolean(betaInstrument)

  const sharedVariables = { modelId, positionSet, base }
  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : {
      ...sharedVariables,
      from,
      to,
      ...(hasBetaInstrument && { betaInstrument }),
    }

  return {
    key: generateQueryKey("beta.multiDate", {
      modelId, positionSet, base, from, to, betaInstrument: variables.betaInstrument, intraday: shouldUseIntradaySimulation || undefined,
    }),
    query: shouldUseIntradaySimulation ? buildLiveTickQuery(betaTypes) : buildDayBasedQuery(betaTypes, hasBetaInstrument),
    variables,
    select: (graphqlResult) => {
      // Day-based simulation.beta is a list of dated rows; live-tick simulation.beta is one IntradayBeta
      // object (aliased date: timestamp) — keyBy needs a list either way.
      const rawBeta = get(graphqlResult, "data.model.simulation.beta", [])
      const beta = keyBy(Array.isArray(rawBeta) ? rawBeta : [rawBeta].filter(Boolean), "date")
      const dynamicBeta = keyBy(get(graphqlResult, "data.model.simulation.dynamicBeta", []), "date")

      const allDates = new Set([...Object.keys(beta), ...Object.keys(dynamicBeta)])
      const byDate = Object.fromEntries(
        [...allDates].map(date => [date, { ...beta[date], ...dynamicBeta[date] }]),
      )

      return {
        values: {
          dates: orderBy(map(byDate, (row, date) => ({
            date,
            ...toBetaPoint(row),
            [CUSTOM_BETA]: row.custom,
          })), "date", "asc"),
        },
      }
    },
  }
}
